import React, { useEffect, useRef, useState } from 'react';
import { EventType } from '../../misc/types';
// Example API call
import { psychologistAppointmentsApi } from '../../api/api';
import { UserSelect } from '../UserSelect';
import { MainUser, MainUserJsonSavedData } from '../../types';
import { useHookstate } from '@hookstate/core';
import { getDateOnly, onInputChangeNative, showSnackbar, updateLocalStateRaw } from '../../misc/utils';
import DefaultButton from '../default-button/DefaultButton';
import Tick from '../../assets/icons/Tick';
import TrashRed from '../../assets/icons/TrashRed';
import { simpleGetter } from '../../actions/common';
import { Avatar } from '../Avatar';
import InputField from '../input-field/InputField';
import ArrowLeft from '../../assets/icons/ArrowLeft';

interface WeeklyCalendarEventModalProps {
  mode: string;
  event: EventType;
  isNew: boolean;
  onClose: (removeNewEvent: boolean) => void;
  onChange: (updated: EventType) => void; // for live update
  onSaveSuccess: () => void; // after backend call
  events: EventType[];
}

function parseTimeToMinutes(t: string): number {
  const [hh, mm] = t.split(':').map(Number);
  return hh * 60 + mm;
}

interface WeeklyCalendarEventModalState {
  selectedUser?: MainUserJsonSavedData;
  psychologistComment?: string;
}


const WeeklyCalendarEventModal: React.FC<WeeklyCalendarEventModalProps> = ({
  mode,
  event,
  isNew,
  onClose,
  onChange,
  onSaveSuccess,
  events
}) => {
  const [loading, setLoading] = useState(false);
  //console.log("events", event);


  const [title, setTitle] = useState(event.title);
  const [start, setStart] = useState(event.start); // "HH:mm"
  const [end, setEnd] = useState(event.end);
  const popupRef = useRef<HTMLDivElement>(null);
  const localState = useHookstate<WeeklyCalendarEventModalState>({});
  const state = localState.get();
  const psychologistComment = state.psychologistComment || "";
  const selectedUser = state.selectedUser || {} as MainUserJsonSavedData;

  const isAdmin = mode === 'admin';

  useEffect(() => {
    if (event.client) {
      updateLocalStateRaw(localState, 'selectedUser')({ ...event.client })
    } else {
      updateLocalStateRaw(localState, 'selectedUser')(null)
    }
  }, [event.id])

  const onSelectMainUser = (user: MainUserJsonSavedData) => {
    updateLocalStateRaw(localState, 'selectedUser')(user);
    updateLocalStateRaw(localState, 'selectedUser_stringified')(JSON.stringify(user));
  }

  const submitComment = async () => {
    const response = await simpleGetter(psychologistAppointmentsApi.addPsychologistComment, {
      id: event.clientSelf?.id,
      comment: psychologistComment
    })
    showSnackbar(response.success ? 'Успешно' : 'Произошла ошибка', response.success ? 'success' : 'error')
    if (response.success) {
      onSaveSuccess();
    }
  }

  useEffect(() => {
    if (event.clientSelf?.additional_data?.postcomments?.psychologist_comment) {
      localState.psychologistComment.set(event.clientSelf.additional_data.postcomments.psychologist_comment)
    }
  }, [event.clientSelf?.additional_data?.postcomments?.psychologist_comment])

  const handleTimeChange = (which: 'start' | 'end', val: string) => {
    const MIN_START = parseTimeToMinutes('07:30'); // 450
    const MAX_END = parseTimeToMinutes('18:30');   // 1110

    // Current numeric times
    const oldStartMins = parseTimeToMinutes(start);
    const oldEndMins = parseTimeToMinutes(end);

    // Proposed new times
    let newStartMins = oldStartMins;
    let newEndMins = oldEndMins;

    if (which === 'start') {
      newStartMins = parseTimeToMinutes(val);
    } else {
      newEndMins = parseTimeToMinutes(val);
    }

    // 1) Check time boundaries
    if (newStartMins < MIN_START) {
      return;
    }
    if (newEndMins > MAX_END) {
      return;
    }
    if (newStartMins >= newEndMins) {
      return;
    }

    // 2) Overlap check with all other events on the same calendar day
    const hasOverlap = events.some((e) => {
      // Skip checking overlap against the same event
      if (e.id === event.id) return false;

      // Make sure it's the same calendar date
      // (compare year/month/day rather than full date-time)
      if (
        e.day.getFullYear() !== event.day.getFullYear() ||
        e.day.getMonth() !== event.day.getMonth() ||
        e.day.getDate() !== event.day.getDate()
      ) {
        return false;
      }

      // Convert to minutes
      const eStartMins = parseTimeToMinutes(e.start);
      const eEndMins = parseTimeToMinutes(e.end);

      // Standard interval overlap check:
      // intervals [newStart, newEnd] & [eStart, eEnd] overlap if:
      // (newStart < eEnd) && (newEnd > eStart)
      return newStartMins < eEndMins && newEndMins > eStartMins;
    });

    if (hasOverlap) {
      return;
    }

    // 3) If OK, update state
    if (which === 'start') {
      setStart(val);
      onChange({ ...event, start: val });
    } else {
      setEnd(val);
      onChange({ ...event, end: val });
    }
  };

  const closeModal = () => {
    onClose(false)
  }

  const handleTitleChange = (val: string) => {
    setTitle(val);
    onChange({ ...event, title: val });
  };

  const handleSave = async () => {
    if (start >= end) {
      return;
    }
    setLoading(true);
    try {
      // Build actual date objects from day + "HH:mm"
      const baseDate = event.day;
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);

      const startDate = new Date(baseDate);
      startDate.setHours(sh, sm, 0, 0);
      const endDate = new Date(baseDate);
      endDate.setHours(eh, em, 0, 0);

      // Example create or update call:
      const body: Record<string, any> = {
        start_time: startDate.toISOString(),
        end_time: endDate.toISOString(),
      }
      if (!isNew) {
        body.id = event.id
      }
      if (selectedUser.id) {
        body.external_user_id = selectedUser.id;
      }
      const response = await simpleGetter(psychologistAppointmentsApi.create, body);
      showSnackbar(response.success ? 'Успешно' : 'Произошла ошибка', response.success ? 'success' : 'error')
      if (response.success) {
        onSaveSuccess();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTimeout(() => onClose(isNew), 200);
  };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const eventTarget = (event.target as HTMLElement)
      if (popupRef.current && !popupRef.current.contains(event.target as Node) && !(eventTarget.classList.contains('calendar-cell') || eventTarget.closest('td.calendar-cell'))) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleDelete = async () => {
    const response = await simpleGetter(psychologistAppointmentsApi.delete, event.id)
    showSnackbar(response.success ? 'Успешно' : 'Произошла ошибка', response.success ? 'success' : 'error')
    if (response.success) {
      onSaveSuccess();
    }
  }

  const isPastSuccessfulMeeting = event.clientSelf?.status === 1 && event.day < new Date()

  const getLabel = () => {
    const meta = {
      title: '',
      showTime: false
    }
    if (!event.clientSelf) {
      meta.title = 'Свободное время'
    } else if (event.clientSelf?.status === -1) {
      meta.title = 'Отменённая встреча'
      meta.showTime = true;
    } else if (event.clientSelf?.status === 2) {
      meta.title = 'Предложенное время'
    } else if (event.clientSelf?.status === 1 && event.day < new Date()) {
      meta.title = 'Прошедшая встреча'
      meta.showTime = true;
    } else {
      meta.title = 'Занятое время'
      meta.showTime = true;
    }

    return meta;
  }

  const eventMeta = getLabel();

  return (
    <div
      ref={popupRef}
      className="p-6 flex flex-col gap-3 bg-white rounded-lg shadow-lg"
    >
      <button
        className="absolute top-2 right-2 text-gray-500"
        onClick={handleClose}
      >
        ×
      </button>
      <div className='flex flex-col gap-0.5 mb-3'>
        <h2 className="text-2xl leading-[26.4px] text-valley-black">
          {eventMeta.title}
        </h2>
        {eventMeta.showTime && (
          <span className='text-[15px] leading-[19.5px] text-valley-gray'>
            {getDateOnly(event.day)}, {event.start}-{event.end}
          </span>
        )}
      </div>

      <div className='flex flex-col gap-8'>
        {event.clientSelf?.additional_data?.postcomments?.client_comment && (
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              <span className='font-medium text-sm leading-[15.4px] text-valley-gray'>
                Комментарий студента
              </span>
              <div className='flex flex-row items-center gap-3'>
                <Avatar user={{ json_saved_data: event.client } as MainUser} />
                <span className='font-medium text-[15px] leading-[19.5px] text-valley-black'>
                  {event.client?.firstname} {event.client?.lastname}
                </span>
              </div>
            </div>
            <span className='text-[15px] leading-[19.5px] text-[#1D1B20]'>
              {event.clientSelf.additional_data.postcomments.client_comment}
            </span>
          </div>
        )}
        {/*@ts-ignore*/}
        {[-1, 1].includes(event.clientSelf?.status) && (
          <React.Fragment>
            <div className='flex flex-col gap-2'>
              <span className='font-medium text-sm leading-[15.4px] text-valley-gray'>
                Ваш комментарий
              </span>
              <InputField
                type="textarea"
                id="psychologist-comment"
                handleChange={onInputChangeNative(localState, "psychologistComment")}
                value={psychologistComment}
                additionalClassName="bg-valley-background-blue py-3 px-4 rounded-lg"
                disabled={isAdmin}
              />
            </div>
            <div className='flex flex-row gap-3'>
              <DefaultButton
                Icon={ArrowLeft}
                buttonText='Вернуться'
                additionalStyle='flex-1 bg-valley-gray text-white py-3'
                onClick={closeModal}
              />
              <DefaultButton
                Icon={Tick}
                buttonText='Сохранить'
                additionalStyle='flex-1 bg-valley-main text-white py-3'
                disabled={isAdmin}
                onClick={submitComment}
              />
            </div>
          </React.Fragment>
        )}
      </div>


      {/* <div className="mb-3">
        <label className="text-sm font-medium">Название</label>
        <input
          className="w-full border p-1 rounded"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
        />
      </div> */}
      {/*@ts-ignore*/}
      {Boolean(!isAdmin && (!event.client || [0, 1, 2].includes((event.clientSelf?.status))) && !isPastSuccessfulMeeting) && (
        <div className="flex gap-2 mb-3">
          <div className="flex-1">
            <label className="text-sm font-medium">Начало</label>
            <input
              type="time"
              className="w-full border p-1 rounded"
              value={start}
              onChange={(e) => handleTimeChange('start', e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="text-sm font-medium">Окончание</label>
            <input
              type="time"
              className="w-full border p-1 rounded"
              value={end}
              onChange={(e) => handleTimeChange('end', e.target.value)}
            />
          </div>
        </div>
      )}
      {[0, 2].includes((event.clientSelf?.status || 0)) && (
        <UserSelect
          label='Бронь на студента'
          placeholder='Выберите студента'
          fieldId='selectPsychologyAppointmentSuggestedUser'
          selectedUser={selectedUser}
          handleChange={onSelectMainUser}
          source='internal'
          editable={!isAdmin}
        />
      )}
      {/*@ts-ignore*/}
      {!isAdmin && !([-1, 1].includes(event.clientSelf?.status)) && (
        <div className='flex flex-row gap-3 mt-5'>
          {!event.client && (
            <DefaultButton
              Icon={TrashRed}
              buttonText='Удалить'
              additionalStyle='bg-valley-red text-white flex-1 py-3 gap-2'
              onClick={handleDelete}
            />
          )}
          <DefaultButton
            Icon={Tick}
            buttonText='Сохранить'
            additionalStyle='bg-valley-main text-white flex-1 py-3 gap-2'
            onClick={handleSave}
          />
        </div>
      )}
    </div>
  );
};

export default WeeklyCalendarEventModal;
